import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, BarChart, Bot, ArrowRight, Clock, UserCheck,
    CalendarDays, BarChart2, Check, ShieldCheck, Zap, Tablet, MonitorCheck,
    ChevronDown, ChevronLeft, ChevronRight, X, MapPin, MousePointer2, AlertTriangle, RefreshCcw, Plane, Filter,
    Sparkles, Brain, BellRing, FileCheck, FileDown, Activity, Contact, Globe, Lock, TrendingUp,
    XCircle, CheckCircle2, SmartphoneNfc, Radio, Send, Loader2, Tag, Linkedin, Facebook, Star,
    Plus, Minus
} from 'lucide-react';
import SEO from '../components/SEO';

const LandingHeader: React.FC = () => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
            if (mobileMenuOpen) setMobileMenuOpen(false);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [mobileMenuOpen]);

    const handleNavClick = (id: string) => {
        setMobileMenuOpen(false);
        setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-3 bg-white/90 backdrop-blur-lg border-b border-[#E8E2D8] shadow-xs' : 'py-6'}`}
            role="banner"
        >
            <div className="container mx-auto px-6 flex justify-between items-center">
                {/* Logo */}
                <a href="/" aria-label="KithHR Home" className="flex items-center space-x-3">
                    <img src="/logo.png" alt="KithHR - Time & Attendance Software Logo" className="w-10 h-10 object-contain" width="40" height="40" fetchPriority="high" />
                    <span className="font-bold text-2xl text-[#0F172A]">
                        KithHR
                    </span>
                </a>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation">
                    <a href="#four-concepts" title="Four Core Benefits" onClick={e => { e.preventDefault(); handleNavClick('four-concepts'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">Benefits</a>
                    <a href="#see-in-action" title="See KithHR in Action" onClick={e => { e.preventDefault(); handleNavClick('see-in-action'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">Product Walkthrough</a>
                    <a href="#comparison" title="KithHR vs Manual" onClick={e => { e.preventDefault(); handleNavClick('comparison'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">KithHR vs Manual</a>
                    <a href="#pricing" title="Pricing" onClick={e => { e.preventDefault(); handleNavClick('pricing'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">Pricing</a>
                    <a href="#resources" title="UK Employer Guides" onClick={e => { e.preventDefault(); handleNavClick('resources'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">Guides</a>
                    <a href="#faq" title="FAQ" onClick={e => { e.preventDefault(); handleNavClick('faq'); }} className="font-semibold text-[#475569] hover:text-[#005BB5] transition">FAQ</a>
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden lg:flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-800 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-200">
                        <Check size={12} strokeWidth={3} /> Free for up to 15 Users
                    </div>
                    <button
                        onClick={() => navigate('/login')}
                        className="hidden md:block bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold px-5 py-2 rounded-full active:scale-95 transition-all shadow-xs"
                    >
                        Sign In
                    </button>
                    {/* Mobile hamburger */}
                    <button
                        className="md:hidden p-2 rounded-xl text-[#0F172A] hover:bg-[#F5F1EB] transition cursor-pointer"
                        onClick={() => setMobileMenuOpen(v => !v)}
                        aria-label="Toggle mobile menu"
                    >
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            {mobileMenuOpen
                                ? <><line x1="4" y1="4" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><line x1="18" y1="4" x2="4" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></>
                                : <><line x1="3" y1="6" x2="19" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><line x1="3" y1="11" x2="19" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><line x1="3" y1="16" x2="19" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></>
                            }
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile Dropdown */}
            {mobileMenuOpen && (
                <nav className="md:hidden bg-white/95 backdrop-blur-xl border-b border-[#E8E2D8] shadow-xl py-4 px-6 flex flex-col gap-1">
                    {[
                        ['four-concepts', 'Benefits'],
                        ['see-in-action', 'Product Walkthrough'],
                        ['comparison', 'KithHR vs Manual'],
                        ['pricing', 'Pricing'],
                        ['resources', 'Guides'],
                        ['faq', 'FAQ']
                    ].map(([id, label]) => (
                        <a key={id} href={`#${id}`} title={label} onClick={e => { e.preventDefault(); handleNavClick(id); }}
                            className="font-semibold text-lg py-3 text-[#0F172A] hover:text-[#005BB5] transition border-b border-[#F5F1EB] last:border-0">
                            {label}
                        </a>
                    ))}
                    <button onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                        className="mt-3 w-full bg-[#0062D2] hover:bg-[#004CB5] text-white font-semibold py-3.5 rounded-2xl active:scale-95 transition-all shadow-md">
                        Start Free →
                    </button>
                </nav>
            )}
        </header>
    );
};

const StickyCTA: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (dismissed) return;
            const scrollPos = window.scrollY;
            const windowHeight = window.innerHeight;
            const bodyHeight = document.documentElement.scrollHeight;
            const isNearFooter = windowHeight + scrollPos >= bodyHeight - 280;
            setVisible(scrollPos > 800 && !isNearFooter);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [dismissed]);

    if (dismissed) return null;

    return (
        <div
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-lg transition-all duration-500 ease-out ${visible ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-24 opacity-0 pointer-events-none'
                }`}
            style={{ visibility: visible ? 'visible' : 'hidden' }}
        >
            <div className="relative bg-white/95 backdrop-blur-xl border border-[#E8E2D8] p-4 rounded-3xl shadow-[0_20px_50px_rgba(30,20,10,0.1)] flex items-center justify-between gap-4">
                <button
                    onClick={() => setDismissed(true)}
                    className="absolute -top-2 -right-2 p-1 bg-[#F5F1EB] border border-[#E8E2D8] text-[#0F172A] rounded-full shadow-md hover:scale-110 active:scale-95 transition cursor-pointer"
                    aria-label="Dismiss banner"
                    title="Dismiss"
                >
                    <X size={14} />
                </button>
                <div className="hidden sm:block">
                    <p className="text-[#0F172A] font-bold text-sm">Ready to simplify your staff timekeeping?</p>
                    <p className="text-[#64748B] text-[10px] font-medium">Free forever for up to 15 employees.</p>
                </div>
                <button
                    onClick={() => window.location.href = '/login'}
                    tabIndex={visible ? 0 : -1}
                    className="flex-1 sm:flex-none bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-3 px-8 rounded-2xl active:scale-95 transition-all shadow-lg shadow-blue-500/20"
                >
                    Start Free
                </button>
            </div>
        </div>
    );
};

const AnimatedSection: React.FC<{ children: React.ReactNode; className?: string; id?: string }> = ({ children, className = '', id }) => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = React.useRef<HTMLElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            id={id}
            className={`py-20 md:py-32 transition-all duration-1000 ease-out ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            } ${className}`}
        >
            {children}
        </section>
    );
};

const FaqItem: React.FC<{ question: string, answer: string }> = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border-b border-[#E8E2D8]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full py-6 flex justify-between items-center text-left focus:outline-none cursor-pointer"
            >
                <span className="text-lg font-bold text-[#0F172A] pr-8">{question}</span>
                <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                    <ChevronDown className="text-[#64748B]" />
                </div>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="pb-6 text-[#475569] leading-relaxed">
                    {answer}
                </p>
            </div>
        </div>
    );
};

const LocalModal: React.FC<{ title: string; content: React.ReactNode; onClose: () => void }> = ({ title, content, onClose }) => (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-[#E8E2D8]">
            <div className="flex justify-between items-center p-6 border-b border-[#E8E2D8]">
                <h2 className="font-bold text-xl text-[#0F172A]">{title}</h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F5F1EB] text-[#64748B] hover:text-[#0F172A] transition" aria-label="Close">
                    <X size={20} />
                </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1 text-[#475569]">{content}</div>
        </div>
    </div>
);

const NfcWaitlistModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [standsNeeded, setStandsNeeded] = useState('1 Stand (Reception / Front Desk)');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError('Please enter a valid work email address.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await addDoc(collection(db, 'nfc_waitlist'), {
                email: email.trim().toLowerCase(),
                companyName: companyName.trim() || 'N/A',
                standsNeeded,
                createdAt: new Date().toISOString(),
                timestamp: serverTimestamp(),
                source: 'landing_page_nfc_section'
            });
            setSubmitted(true);
        } catch (err: any) {
            console.error('Waitlist submission error:', err);
            setSubmitted(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Join NFC Hardware Waitlist">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
            <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-[#E8E2D8] animate-fade-in">
                <div className="flex justify-between items-center p-6 border-b border-[#E8E2D8] bg-[#F5F1EB]/50">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-blue-50 text-[#005BB5] rounded-xl">
                            <SmartphoneNfc size={22} />
                        </div>
                        <div>
                            <h2 className="font-extrabold text-lg text-[#0F172A]">KithHR NFC Desk Plaque</h2>
                            <p className="text-xs text-[#64748B]">Official Hardware Priority Waitlist (Coming Soon)</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F5F1EB] text-[#64748B] hover:text-[#0F172A] transition" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 md:p-8">
                    {submitted ? (
                        <div className="text-center py-6 space-y-4">
                            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                                <CheckCircle2 size={36} />
                            </div>
                            <h3 className="text-2xl font-black text-[#0F172A]">You're on the Priority List!</h3>
                            <p className="text-sm text-[#475569] max-w-sm mx-auto leading-relaxed">
                                Thank you! We have reserved your spot for the official KithHR Acrylic NFC Desk Stands. Our team will contact you at <strong className="text-[#005BB5] font-bold">{email}</strong> before public shipment!
                            </p>
                            <div className="pt-4">
                                <button onClick={onClose} className="w-full bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-3.5 px-6 rounded-2xl shadow-lg transition-all">
                                    Done
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <p className="text-sm text-[#475569] leading-relaxed">
                                Reserve your pre-printed acrylic desk plaque. Pre-orders will include instant dashboard pairing.
                            </p>

                            {error && (
                                <div className="p-3 rounded-xl bg-red-50 text-red-600 text-xs font-semibold">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label htmlFor="nfc-email" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                                    Work Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="nfc-email"
                                    aria-label="Work Email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="manager@yourcompany.co.uk"
                                    className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E2D8] rounded-xl text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0062D2] text-sm"
                                />
                            </div>

                            <div>
                                <label htmlFor="nfc-company-name" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                                    Company / Business Name
                                </label>
                                <input
                                    id="nfc-company-name"
                                    aria-label="Company or Business Name"
                                    type="text"
                                    value={companyName}
                                    onChange={e => setCompanyName(e.target.value)}
                                    placeholder="e.g. Apex Logistics Ltd"
                                    className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E2D8] rounded-xl text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0062D2] text-sm"
                                />
                            </div>

                            <div>
                                <label htmlFor="nfc-stands-needed" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                                    Estimated Desk Stands Needed
                                </label>
                                <select
                                    id="nfc-stands-needed"
                                    aria-label="Estimated Desk Stands Needed"
                                    value={standsNeeded}
                                    onChange={e => setStandsNeeded(e.target.value)}
                                    className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E2D8] rounded-xl text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0062D2] text-sm cursor-pointer"
                                >
                                    <option value="1 Stand (Reception / Front Desk)">1 Stand (Reception / Main Entrance)</option>
                                    <option value="2-5 Stands (Multiple Sites / Entrances)">2-5 Stands (Multiple Sites / Entrances)</option>
                                    <option value="6-15 Stands (Multi-Department / Facility)">6-15 Stands (Multi-Department / Facility)</option>
                                    <option value="15+ Enterprise Custom Deployment">15+ Enterprise Custom Deployment</option>
                                </select>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-4 px-6 rounded-2xl shadow-xl transition-all flex items-center justify-center gap-2 text-base disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" /> Reserving...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={18} /> Join Hardware Waitlist →
                                        </>
                                    )}
                                </button>
                                <p className="text-center text-[11px] text-[#64748B] mt-2.5">
                                    No commitment or payment required today.
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

// Apple-Grade Interactive Product Stage Showcase
interface ShowcaseNarrativeItem {
    id: string;
    tabLabel: string;
    badge: string;
    badgeColor: string;
    headline: string;
    subtext: string;
    imageSrc: string;
    imageAlt: string;
    highlights: {
        icon: any;
        label: string;
        desc: string;
        metric?: string;
    }[];
    ctaText: string;
}

const SHOWCASE_ITEMS: ShowcaseNarrativeItem[] = [
    {
        id: 'tracking',
        tabLabel: 'Real-Time Tracking',
        badge: 'LIVE WORKFORCE RADAR',
        badgeColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        headline: 'Monitor Team Locations & Live Attendance',
        subtext: 'Maintain real-time operational visibility across all active work sites. Spot perimeter breaches, view operational metrics, and check active team statuses live.',
        imageSrc: '/assets/showcase/kithhr-real-time-tracking.jpg',
        imageAlt: 'KithHR Real-Time GPS Tracking and Geofence Breach Radar',
        ctaText: 'Explore Real-Time Tracking',
        highlights: [
            {
                icon: MapPin,
                label: 'Active Team GPS Radar',
                desc: 'Track en-route status and pinpoint exact employee clock-in locations.',
                metric: 'Live Radar'
            },
            {
                icon: ShieldCheck,
                label: 'Automated Breach Alerts',
                desc: 'Instant notifications when clock-ins occur outside designated site geofences.',
                metric: 'Instant'
            },
            {
                icon: Activity,
                label: 'Systems Operational Status',
                desc: 'Live telemetry across all workforce services, metrics, and active rosters.',
                metric: '100% Uptime'
            },
            {
                icon: SmartphoneNfc,
                label: 'Mobile & Tablet Ready',
                desc: 'Compatible with standard smartphones, Google Play app, and web kiosks.',
                metric: 'Google Play'
            }
        ]
    },
    {
        id: 'all-in-one',
        tabLabel: 'All-in-One HR App',
        badge: 'UNIFIED WORKFORCE ECOSYSTEM',
        badgeColor: 'bg-blue-50 text-blue-800 border-blue-200',
        headline: 'All-in-One HR Manager & Employee App',
        subtext: 'Eliminate fragmented tools. Manage your entire workforce and give employees 24/7 access to shifts, leaves, and timekeeping in one cohesive application.',
        imageSrc: '/assets/showcase/kithhr-all-in-one-app.jpg',
        imageAlt: 'KithHR All-in-One HR Manager and Employee App',
        ctaText: 'Explore All-in-One Suite',
        highlights: [
            {
                icon: Clock,
                label: "Today's Attendance & Punch In",
                desc: 'Zero-friction punch-in with verified timestamps and live status indicators.',
                metric: '1-Tap Punch'
            },
            {
                icon: CalendarDays,
                label: 'Quick Employee Actions',
                desc: 'Fast self-service access to leave requests, digital payslips, and personal rotas.',
                metric: 'Self-Service'
            },
            {
                icon: BellRing,
                label: 'Announcements & Updates',
                desc: 'Keep every team member aligned with instant company policy and site notices.',
                metric: 'Push Alerts'
            },
            {
                icon: UserCheck,
                label: 'Leave Status Workflows',
                desc: 'Transparent visual rings showing pending approvals, remaining days, and history.',
                metric: 'Real-Time'
            }
        ]
    },
    {
        id: 'freedom',
        tabLabel: 'Employee Freedom',
        badge: 'SELF-SERVICE AUTONOMY',
        badgeColor: 'bg-indigo-50 text-indigo-800 border-indigo-200',
        headline: 'Clock In Securely & Manage Shifts 24/7',
        subtext: 'Staff clock in securely with a unique 4-digit PIN or local device verification, view upcoming shift rotas, and request holidays without paperwork delays.',
        imageSrc: '/assets/showcase/kithhr-employee-freedom.jpg',
        imageAlt: 'KithHR Employee Freedom Self-Service and PIN Clock-In',
        ctaText: 'Explore Employee Freedom',
        highlights: [
            {
                icon: Lock,
                label: 'Secure 4-Digit PIN Clock-In',
                desc: 'Zero-hardware attendance verification that completely prevents buddy punching.',
                metric: 'Zero Fraud'
            },
            {
                icon: CalendarDays,
                label: 'Upcoming Shifts & Rotas',
                desc: 'Color-coded shift calendar with instant sync to Google Calendar and Apple iCal.',
                metric: '24/7 Access'
            },
            {
                icon: Plane,
                label: 'Instant Holiday Requests',
                desc: 'Submit statutory leave anytime with automated 12.07% UK holiday accrual math.',
                metric: '12.07% Calc'
            },
            {
                icon: Users,
                label: 'Dual Manager & Staff Switch',
                desc: 'Clear single sign-on separating manager admin privileges from employee self-service.',
                metric: 'Dual Role'
            }
        ]
    }
];

const HeroProductShowcaseStage: React.FC = () => {
    const navigate = useNavigate();
    const [activeIdx, setActiveIdx] = useState<number>(0);
    const [selectedModalItem, setSelectedModalItem] = useState<ShowcaseNarrativeItem | null>(null);
    const touchStartX = React.useRef<number | null>(null);

    const currentItem = SHOWCASE_ITEMS[activeIdx];

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX.current - touchEndX;

        if (Math.abs(diff) > 40) {
            if (diff > 0) {
                setActiveIdx((prev) => (prev + 1) % SHOWCASE_ITEMS.length);
            } else {
                setActiveIdx((prev) => (prev - 1 + SHOWCASE_ITEMS.length) % SHOWCASE_ITEMS.length);
            }
        }
        touchStartX.current = null;
    };

    return (
        <div className="mt-10 md:mt-16 relative w-full max-w-6xl mx-auto px-3 md:px-4 select-none">
            {/* Header Badge */}
            <div className="flex items-center justify-center gap-2 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white text-teal-800 text-xs font-black uppercase tracking-widest border border-[#E8E2D8] shadow-xs">
                    <Sparkles size={14} className="animate-spin text-teal-600" /> Interactive Product Suite • Apple-Grade Showcase
                </div>
            </div>

            {/* Apple Pill Tabs */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8 md:mb-10">
                {SHOWCASE_ITEMS.map((item, idx) => {
                    const isActive = activeIdx === idx;
                    return (
                        <button
                            key={item.id}
                            onClick={() => setActiveIdx(idx)}
                            className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl text-xs sm:text-sm font-bold transition-all duration-300 flex items-center gap-2.5 cursor-pointer shadow-xs ${
                                isActive
                                    ? 'bg-[#0F172A] text-white shadow-lg shadow-slate-900/10 scale-102 ring-2 ring-[#0062D2]/30'
                                    : 'bg-white text-[#475569] hover:text-[#0F172A] hover:bg-[#F5F1EB] border border-[#E8E2D8]'
                            }`}
                        >
                            <span className={`w-2 h-2 rounded-full transition-colors ${isActive ? 'bg-[#0062D2]' : 'bg-slate-300'}`} />
                            {item.tabLabel}
                        </button>
                    );
                })}
            </div>

            {/* Stage Grid Container */}
            <div
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                className="bg-white rounded-3xl md:rounded-[2.5rem] border border-[#E8E2D8] shadow-[0_20px_50px_rgba(30,20,10,0.06)] p-5 sm:p-8 lg:p-10 transition-all duration-300"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                    
                    {/* Left Column: Large High-Resolution Poster Card */}
                    <div className="lg:col-span-6 flex flex-col items-center">
                        <div
                            onClick={() => setSelectedModalItem(currentItem)}
                            className="relative w-full max-w-[430px] rounded-2xl md:rounded-3xl overflow-hidden bg-[#FAF8F5] border border-[#E8E2D8] shadow-md group cursor-pointer hover:shadow-xl hover:border-[#0062D2]/50 transition-all duration-300"
                        >
                            <img
                                key={currentItem.id}
                                src={currentItem.imageSrc}
                                alt={currentItem.imageAlt}
                                className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02] animate-fade-in"
                                loading="eager"
                                fetchPriority="high"
                            />

                            {/* Hover Inspect Pill */}
                            <div className="absolute inset-0 bg-slate-900/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-6">
                                <span className="bg-white/95 backdrop-blur-md text-[#0F172A] text-xs font-bold px-4 py-2 rounded-full shadow-lg border border-[#E8E2D8] flex items-center gap-1.5 transition-transform group-hover:scale-105">
                                    <MonitorCheck size={15} className="text-[#005BB5]" /> Click to Inspect High-Res
                                </span>
                            </div>
                        </div>

                        {/* Slide Stepper Controls */}
                        <div className="mt-4 flex items-center gap-4 text-xs font-bold text-[#64748B]">
                            <button
                                onClick={() => setActiveIdx((prev) => (prev - 1 + SHOWCASE_ITEMS.length) % SHOWCASE_ITEMS.length)}
                                className="p-2 rounded-full bg-white hover:bg-[#F5F1EB] active:scale-95 text-[#0F172A] border border-[#E8E2D8] shadow-xs transition cursor-pointer"
                                aria-label="Previous showcase screen"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="font-mono text-[11px] uppercase tracking-wider text-[#005BB5] font-bold">
                                {activeIdx + 1} / {SHOWCASE_ITEMS.length} • {currentItem.tabLabel}
                            </span>
                            <button
                                onClick={() => setActiveIdx((prev) => (prev + 1) % SHOWCASE_ITEMS.length)}
                                className="p-2 rounded-full bg-white hover:bg-[#F5F1EB] active:scale-95 text-[#0F172A] border border-[#E8E2D8] shadow-xs transition cursor-pointer"
                                aria-label="Next showcase screen"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Side-by-Side Feature Narrative */}
                    <div key={currentItem.id} className="lg:col-span-6 flex flex-col justify-center text-left animate-fade-in">
                        {/* Category Badge */}
                        <div className="mb-3">
                            <span className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border shadow-2xs ${currentItem.badgeColor}`}>
                                <CheckCircle2 size={13} /> {currentItem.badge}
                            </span>
                        </div>

                        {/* Headline & Subtext */}
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#0F172A] tracking-tight leading-snug mb-3">
                            {currentItem.headline}
                        </h2>
                        <p className="text-sm sm:text-base text-[#475569] leading-relaxed font-normal mb-6">
                            {currentItem.subtext}
                        </p>

                        {/* Feature Highlights Stack */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-8">
                            {currentItem.highlights.map((feat, fIdx) => {
                                const IconComponent = feat.icon;
                                return (
                                    <div
                                        key={fIdx}
                                        className="p-3.5 rounded-2xl bg-[#FAF8F5] border border-[#E8E2D8] flex flex-col justify-between hover:bg-white hover:border-[#0062D2]/40 hover:shadow-sm transition-all"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#005BB5] flex items-center justify-center shrink-0">
                                                <IconComponent size={16} />
                                            </div>
                                            {feat.metric && (
                                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-white border border-[#E8E2D8] text-teal-800">
                                                    {feat.metric}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-bold text-[#0F172A] mb-1">
                                                {feat.label}
                                            </h3>
                                            <p className="text-[11px] text-[#475569] leading-relaxed">
                                                {feat.desc}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* CTA Row */}
                        <div className="flex flex-col sm:flex-row items-center gap-4 pt-2 border-t border-slate-100">
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-3.5 px-7 rounded-2xl shadow-lg shadow-blue-500/20 active:scale-95 transition-all text-sm flex items-center justify-center gap-2 cursor-pointer"
                            >
                                {currentItem.ctaText} <ArrowRight size={16} />
                            </button>
                            <div className="flex items-center gap-2 text-xs font-bold text-[#64748B]">
                                <Check size={14} className="text-emerald-600 font-extrabold" /> Free up to 15 employees
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* High-Resolution Inspection Modal */}
            {selectedModalItem && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-label={selectedModalItem.headline}>
                    <div className="absolute inset-0 bg-black/85 backdrop-blur-md animate-fade-in" onClick={() => setSelectedModalItem(null)} />
                    <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden border border-[#E8E2D8] z-10 animate-scale-up max-h-[92vh] flex flex-col">
                        <div className="flex justify-between items-center px-6 py-4 bg-[#FAF8F5] border-b border-[#E8E2D8]">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-blue-50 text-[#005BB5]">
                                    <MonitorCheck size={20} />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-base text-[#0F172A]">{selectedModalItem.headline}</h3>
                                    <p className="text-xs text-[#64748B]">{selectedModalItem.tabLabel} — KithHR Official Suite</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedModalItem(null)}
                                className="p-2 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition cursor-pointer"
                                aria-label="Close high-res inspection"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 md:p-6 overflow-y-auto bg-[#F5F1EB] flex-1 flex items-center justify-center">
                            <img
                                src={selectedModalItem.imageSrc}
                                alt={selectedModalItem.imageAlt}
                                className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-xl border border-[#E8E2D8]"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const HeroMockups: React.FC = () => {
    return <HeroProductShowcaseStage />;
};

// 3. FOUR CORE BENEFITS COMPONENT
const FourCoreBenefits: React.FC = () => {
    const navigate = useNavigate();
    const [selectedConcept, setSelectedConcept] = useState<any | null>(null);

    const concepts = [
        {
            number: "01",
            title: "CLOCK IN",
            tagline: "Digital employee clock-in and clock-out.",
            description: "Staff clock in using a secure 4-digit PIN on any phone, tablet, or web browser. Works offline if site Wi-Fi drops.",
            icon: Clock,
            color: "from-blue-500 to-cyan-500",
            details: {
                headline: "Zero-Friction Staff Clock-In & Verification",
                subtext: "Multiple ways for your workforce to register attendance accurately with zero buddy punching.",
                badge: "Native Android & PWA Supported",
                features: [
                    "4-Digit PIN Verification: Secure individual PIN entry for instant clock-in & clock-out.",
                    "PWA & Native Android App: Works in any web browser or via the Google Play Store app.",
                    "Offline Queueing Resilience: Timestamps cache locally when site Wi-Fi drops and auto-sync when back online.",
                    "GPS Site Geofencing: Verification that staff are physically within the designated site perimeter.",
                    "Optional Photo & Signature Kiosk: Capture clock-in proof stored securely for manager audit logs."
                ]
            }
        },
        {
            number: "02",
            title: "SCHEDULE",
            tagline: "Create and manage employee shifts and rotas.",
            description: "Build weekly or monthly rotas with a visual calendar. Instantly spot shift overlaps and notify your team.",
            icon: CalendarDays,
            color: "from-purple-500 to-indigo-500",
            details: {
                headline: "Drag-and-Drop Shift Rotas & Rota Scheduling",
                subtext: "Publish rotas in seconds, prevent shift conflicts, and keep employees informed on their mobile devices.",
                badge: "iCal Google & Apple Sync",
                features: [
                    "Visual Calendar Rota Builder: Drag and drop shifts across departments and employee roles.",
                    "Double-Booking Alerts: Automatic warnings if an employee is scheduled across overlapping sites.",
                    "iCal Calendar Feeds: Employees sync their shifts directly to Google Calendar, Apple iCal, or Outlook.",
                    "Holiday & Absence Overlays: Approved leave and sick days block rota conflicts automatically.",
                    "Shift Publishing Notifications: Send instant shift alerts to your team."
                ]
            }
        },
        {
            number: "03",
            title: "TRACK",
            tagline: "Monitor attendance, hours and workforce activity.",
            description: "Keep complete visibility of who is working across all sites. Track breaks, overtime, and punctuality in real-time.",
            icon: MapPin,
            color: "from-emerald-500 to-teal-500",
            details: {
                headline: "Live Site Radar & Real-Time Attendance Intelligence",
                subtext: "Monitor live workforce activity, track labor cost burn rates, and maintain full site visibility.",
                badge: "Live Labor Cost Radar",
                features: [
                    "Live Site Attendance Radar: View currently clocked-in staff across all company locations in real time.",
                    "Real-Time Labor Burn Rate: Live monetary cost accumulation vs daily shift budgets.",
                    "Automated Break Deductions: Pre-configured break rules calculated automatically.",
                    "Punctuality & Late Alerts: Spot tardiness patterns early before site operations are impacted.",
                    "Staff Logbook History: Digital audit trail for staff performance notes, warnings, and commendations."
                ]
            }
        },
        {
            number: "04",
            title: "PAYROLL",
            tagline: "Generate clear, payroll-ready reports.",
            description: "Automate gross pay calculations and break deductions. Export accountant-ready PDF and CSV summaries in one click.",
            icon: BarChart,
            color: "from-amber-500 to-orange-500",
            details: {
                headline: "Accountant-Ready Timesheet & Gross Pay Reports",
                subtext: "Turn raw clock-in timestamps into pre-payroll calculations aligned with UK employment standards.",
                badge: "12.07% UK Holiday Accrual Built-In",
                features: [
                    "UK Statutory Holiday Accrual (12.07%): Automatic leave entitlement calculations for part-time & irregular hours staff.",
                    "Gross Pay Math: Calculated by employee hourly pay rates, overtime multipliers, and break deductions.",
                    "1-Click PDF & CSV Exports: Accountant-ready timesheet drafts formatted for quick processing.",
                    "Direct Xero Integration: Sync timesheets directly to Xero Payroll with one click.",
                    "Immutable Audit Trail: Digital log of every timesheet edit and manager approval."
                ]
            }
        }
    ];

    const scrollToFeatures = () => {
        document.getElementById('all-features')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <AnimatedSection className="bg-[#FAF8F5] py-24" id="four-concepts">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-xs font-black uppercase tracking-widest mb-4">
                        Core Capabilities
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        Everything your team needs to manage attendance
                    </h2>
                    <p className="text-lg text-[#475569] font-medium leading-relaxed">
                        Four practical concepts that replace paper timesheets, WhatsApp rota groups, and manual payroll calculations. Click any card for full details.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {concepts.map(concept => (
                        <div 
                            key={concept.title}
                            onClick={() => setSelectedConcept(concept)}
                            className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-[#E8E2D8] shadow-lg hover:shadow-2xl hover:border-teal-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer flex flex-col justify-between group"
                        >
                            <div>
                                <div className="flex items-center justify-between mb-6">
                                    <div className={`p-4 rounded-2xl bg-gradient-to-br ${concept.color} text-white shadow-md group-hover:scale-110 transition-transform`}>
                                        <concept.icon size={26} />
                                    </div>
                                    <span className="text-3xl font-black text-[#94A3B8] tracking-wider">
                                        {concept.number}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-extrabold text-[#0F172A] mb-2 group-hover:text-[#0D9488] transition-colors">
                                    {concept.title}
                                </h3>
                                <p className="text-base font-bold text-blue-600 mb-4">
                                    {concept.tagline}
                                </p>
                                <p className="text-[#475569] text-sm leading-relaxed mb-6">
                                    {concept.description}
                                </p>
                            </div>

                            <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-blue-700 group-hover:underline">
                                <span>Click for Full Feature Details</span>
                                <span>→</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-12">
                    <button
                        onClick={scrollToFeatures}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0F172A] font-bold rounded-2xl border border-[#E8E2D8] shadow-xs hover:bg-[#F5F1EB] transition"
                    >
                        View All Features ↓
                    </button>
                </div>
            </div>

            {/* INTERACTIVE FEATURE SPOTLIGHT MODAL */}
            {selectedConcept && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={() => setSelectedConcept(null)} />
                    <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-[#E8E2D8] animate-fade-in max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b border-[#E8E2D8] bg-[#F5F1EB]/50">
                            <div className="flex items-center gap-3">
                                <div className={`p-3 rounded-2xl bg-gradient-to-br ${selectedConcept.color} text-white shadow-md`}>
                                    <selectedConcept.icon size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-wider text-[#005BB5]">Pillar {selectedConcept.number} — {selectedConcept.title}</div>
                                    <h2 className="text-xl font-extrabold text-[#0F172A]">{selectedConcept.details.headline}</h2>
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedConcept(null)} 
                                className="p-2 rounded-full hover:bg-[#F5F1EB] text-[#64748B] hover:text-[#0F172A] transition"
                                aria-label="Close feature modal"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 md:p-8 overflow-y-auto space-y-6">
                            <p className="text-sm text-[#475569] font-medium leading-relaxed">
                                {selectedConcept.details.subtext}
                            </p>

                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200">
                                <CheckCircle2 size={14} /> {selectedConcept.details.badge}
                            </div>

                            <div className="space-y-3 pt-2">
                                <h3 className="text-xs font-black uppercase tracking-wider text-[#64748B]">Detailed Feature Breakdown:</h3>
                                <ul className="space-y-3">
                                    {selectedConcept.details.features.map((feat: string, idx: number) => {
                                        const [title, desc] = feat.split(': ');
                                        return (
                                            <li key={idx} className="flex items-start gap-3 bg-[#FAF8F5] p-3.5 rounded-2xl border border-[#E8E2D8] text-sm">
                                                <Check size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-[#0F172A] font-bold">{title}:</strong>{' '}
                                                    <span className="text-[#475569]">{desc}</span>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>

                        <div className="p-6 border-t border-[#E8E2D8] bg-[#F5F1EB]/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <span className="text-xs text-[#64748B]">Free forever for up to 15 employees.</span>
                            <button
                                onClick={() => {
                                    setSelectedConcept(null);
                                    navigate('/login');
                                }}
                                className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-6 py-3 rounded-2xl shadow-lg transition-all text-sm flex items-center justify-center gap-2"
                            >
                                Start Free with {selectedConcept.title} →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AnimatedSection>
    );
};

// 4.5 WHATSAPP FRONTLINE ASSISTANT SPOTLIGHT
const WhatsAppAssistantSpotlight: React.FC = () => {
    const navigate = useNavigate();

    return (
        <AnimatedSection className="py-20 md:py-28 bg-[#FAF8F5]" id="whatsapp-assistant">
            <div className="container mx-auto px-6 max-w-6xl">
                {/* Header Badge */}
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-black uppercase tracking-widest mb-4 shadow-xs">
                        <Sparkles size={14} className="text-emerald-600 animate-spin" /> Coming Soon • Private Beta
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        The Zero-App Frontline Experience: WhatsApp Assistant
                    </h2>
                    <p className="text-base sm:text-lg text-[#475569] font-medium leading-relaxed">
                        Free your managers from repetitive rota texts and manual leave calculations. Staff check schedules and holiday balances straight in WhatsApp — with zero app downloads.
                    </p>
                </div>

                {/* Main Card Grid */}
                <div className="bg-white rounded-3xl md:rounded-[2.5rem] border border-[#E8E2D8] shadow-[0_20px_50px_rgba(30,20,10,0.06)] p-6 sm:p-10 lg:p-12 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        
                        {/* Left: Value Points */}
                        <div className="lg:col-span-6 space-y-6">
                            <div className="space-y-4">
                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 shadow-2xs">
                                        <Bot size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mb-1">
                                            100% Zero App Downloads for Staff
                                        </h3>
                                        <p className="text-xs sm:text-sm text-[#475569] leading-relaxed">
                                            Frontline workers (kitchen staff, cleaners, drivers, carers) rarely download heavy corporate apps. With KithHR, they interact via the WhatsApp they already use every day.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-2xl bg-blue-50 text-[#005BB5] flex items-center justify-center shrink-0 border border-blue-100 shadow-2xs">
                                        <CalendarDays size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mb-1">
                                            Instant Rota &amp; 12.07% Holiday Lookups
                                        </h3>
                                        <p className="text-xs sm:text-sm text-[#475569] leading-relaxed">
                                            Employees simply text <span className="font-semibold text-[#0F172A]">"When is my next shift?"</span> or <span className="font-semibold text-[#0F172A]">"What is my holiday balance?"</span> and receive verified answers in 2 seconds.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 border border-purple-100 shadow-2xs">
                                        <BellRing size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mb-1">
                                            Real-Time Sickness &amp; Absence Alerts
                                        </h3>
                                        <p className="text-xs sm:text-sm text-[#475569] leading-relaxed">
                                            Staff report unexpected sickness via WhatsApp. Managers receive immediate live alerts, allowing instant shift cover before site operations suffer.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Anti-Fraud Attendance Notice */}
                            <div className="p-4 rounded-2xl bg-[#FAF8F5] border border-amber-200/80 flex items-start gap-3">
                                <ShieldCheck size={22} className="text-amber-700 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-0.5">
                                        Protected Attendance (Zero Buddy Punching)
                                    </h4>
                                    <p className="text-xs text-[#475569] leading-relaxed">
                                        To eliminate fraud and prevent off-site buddy punching, <strong>Clock In &amp; Out remains strictly verified on-site</strong> via our touchscreen Tablet PIN Kiosk or GPS Mobile App.
                                    </p>
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="pt-2 flex flex-col sm:flex-row items-center gap-4">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-3.5 px-7 rounded-2xl shadow-lg shadow-blue-500/20 active:scale-95 transition-all text-sm flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    Start Free &amp; Join Private Beta <ArrowRight size={16} />
                                </button>
                                <span className="text-xs font-bold text-[#64748B]">
                                    Free up to 15 employees · 0 Contracts
                                </span>
                            </div>
                        </div>

                        {/* Right: WhatsApp Chat UI Simulation */}
                        <div className="lg:col-span-6 flex justify-center">
                            <div className="w-full max-w-sm rounded-[2rem] overflow-hidden bg-[#EFEAE2] border-4 border-slate-900/10 shadow-2xl flex flex-col text-xs font-sans">
                                
                                {/* WhatsApp Header */}
                                <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3 shadow-md">
                                    <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                                        <Bot size={18} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-bold text-sm flex items-center gap-1.5">
                                            KithHR Assistant <CheckCircle2 size={13} className="text-emerald-300" />
                                        </div>
                                        <div className="text-[10px] text-emerald-100 flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                            Official AI Assistant · Online
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Canvas */}
                                <div className="p-4 space-y-3 bg-[#ECE5DD] min-h-[340px] flex flex-col justify-end">
                                    {/* Bubble 1: Employee */}
                                    <div className="flex justify-end">
                                        <div className="bg-[#DCF8C6] text-[#0F172A] p-2.5 rounded-2xl rounded-tr-xs shadow-2xs max-w-[85%] leading-relaxed">
                                            <p className="font-medium">When is my next shift?</p>
                                            <span className="block text-[9px] text-slate-500 text-right mt-1">09:14</span>
                                        </div>
                                    </div>

                                    {/* Bubble 2: Assistant */}
                                    <div className="flex justify-start">
                                        <div className="bg-white text-[#0F172A] p-3 rounded-2xl rounded-tl-xs shadow-2xs max-w-[88%] leading-relaxed space-y-1">
                                            <p className="font-semibold text-emerald-800">Hi Alex! 👋</p>
                                            <p className="text-[11px] text-[#334155]">Your next shift is <strong className="text-[#0F172A]">Friday, 17 Oct</strong> at <strong className="text-[#0F172A]">08:00 – 16:30</strong> (Main Kitchen).</p>
                                            <p className="text-[10px] text-slate-500">Total shifts scheduled this week: <strong>4 shifts</strong>.</p>
                                            <span className="block text-[9px] text-slate-400 text-right">09:14</span>
                                        </div>
                                    </div>

                                    {/* Bubble 3: Employee */}
                                    <div className="flex justify-end">
                                        <div className="bg-[#DCF8C6] text-[#0F172A] p-2.5 rounded-2xl rounded-tr-xs shadow-2xs max-w-[85%] leading-relaxed">
                                            <p className="font-medium">What is my holiday balance?</p>
                                            <span className="block text-[9px] text-slate-500 text-right mt-1">09:15</span>
                                        </div>
                                    </div>

                                    {/* Bubble 4: Assistant */}
                                    <div className="flex justify-start">
                                        <div className="bg-white text-[#0F172A] p-3 rounded-2xl rounded-tl-xs shadow-2xs max-w-[88%] leading-relaxed space-y-1">
                                            <p className="text-[11px] text-[#334155]">🌴 Under your UK 12.07% accrual, you currently have <strong className="text-emerald-700">28.4 hours</strong> of statutory holiday available to book.</p>
                                            <span className="block text-[9px] text-slate-400 text-right">09:15</span>
                                        </div>
                                    </div>

                                    {/* Safety Banner Inside Chat */}
                                    <div className="text-center py-1">
                                        <span className="bg-amber-100 text-amber-900 text-[10px] font-semibold px-2.5 py-1 rounded-full border border-amber-200">
                                            🔒 Clock-in requires on-site Tablet PIN Kiosk
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 5. SEE KITHHR IN ACTION (PRODUCT PROOF)
const SeeKithHRInAction: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'clockin' | 'schedule' | 'track' | 'payroll'>('clockin');
    const [clockInView, setClockInView] = useState<'sidebar' | 'terminal'>('sidebar');

    // Auto-rotate clock-in preview images every 4 seconds
    useEffect(() => {
        if (activeTab !== 'clockin') return;
        const timer = setInterval(() => {
            setClockInView(prev => (prev === 'sidebar' ? 'terminal' : 'sidebar'));
        }, 4000);
        return () => clearInterval(timer);
    }, [activeTab]);

    const tabData = {
        clockin: {
            title: "Digital PIN Clock-In Kiosk",
            subtitle: "Staff clock in or out in seconds using a secure 4-digit PIN on any browser or tablet.",
            highlights: [
                "Runs on any existing smartphone, tablet, or web browser",
                "Instant local PIN verification",
                "Works offline when site Wi-Fi drops out",
                "GPS site geofencing for remote or multi-site verification"
            ],
            image: clockInView === 'sidebar' ? "/assets/screenshots/kiosk-sidebar.jpg" : "/assets/screenshots/kiosk-terminal.jpg"
        },
        schedule: {
            title: "Drag-and-Drop Shift Rota Manager",
            subtitle: "Build weekly or monthly shift schedules without confusing spreadsheets.",
            highlights: [
                "Visual shift calendar for multi-department planning",
                "Automatic warnings for double-booked employees",
                "Rotating shift patterns (e.g. 4-on-4-off)",
                "Publish rotas with instant calendar sync"
            ],
            image: "/assets/screenshots/hero-calendar.png"
        },
        track: {
            title: "Real-Time Attendance & Live Site Map",
            subtitle: "Monitor who is on-site across all business locations right now.",
            highlights: [
                "Live site attendance radar",
                "Automated exception alerts for missed clock-ins",
                "Holiday, sickness, and statutory absence logging",
                "Full digital audit trail of timesheet changes"
            ],
            image: "/assets/screenshots/hero-livemap.png"
        },
        payroll: {
            title: "Payroll-Ready Gross Pay Summaries",
            subtitle: "The complex timesheet math done automatically in seconds.",
            highlights: [
                "Automatic gross hours, overtime, and break deductions",
                "1-click PDF and CSV exports for your accountant",
                "Supports holiday calculations for eligible irregular-hours workers using statutory rules",
                "Complete UK GDPR-compliant record-keeping"
            ],
            image: "/assets/screenshots/hero-payroll.png"
        }
    };

    const current = tabData[activeTab];

    return (
        <AnimatedSection className="bg-[#FAF8F5] py-24" id="see-in-action">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-800 text-xs font-black uppercase tracking-widest border border-blue-200 mb-4">
                        Product Walkthrough
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        See KithHR in Action
                    </h2>
                    <p className="text-lg text-[#475569] font-medium leading-relaxed">
                        Everything you need to manage staff time, shifts and attendance in one place.
                    </p>
                </div>

                {/* Walkthrough Selector Tabs */}
                <div className="flex flex-wrap justify-center items-center gap-3 mb-12">
                    {[
                        { id: 'clockin', label: '1. Clock In' },
                        { id: 'schedule', label: '2. Schedule' },
                        { id: 'track', label: '3. Track' },
                        { id: 'payroll', label: '4. Payroll' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 ${activeTab === tab.id
                                ? 'bg-[#0062D2] text-white shadow-lg shadow-blue-500/25 scale-105'
                                : 'bg-white text-[#334155] border border-[#E8E2D8] hover:bg-[#F5F1EB] shadow-xs'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Showcase Box */}
                <div className="max-w-5xl mx-auto bg-white rounded-[2.5rem] border border-[#E8E2D8] p-8 md:p-12 shadow-xl">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        {/* Info Column */}
                        <div className="lg:col-span-5 space-y-6 text-left">
                            <h3 className="text-3xl font-extrabold text-[#0F172A]">
                                {current.title}
                            </h3>
                            <p className="text-[#475569] text-base leading-relaxed">
                                {current.subtitle}
                            </p>

                            <ul className="space-y-3 pt-2">
                                {current.highlights.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <div className="p-1 bg-emerald-50 text-emerald-700 rounded-full shrink-0 mt-0.5 border border-emerald-200">
                                            <Check size={14} strokeWidth={3} />
                                        </div>
                                        <span className="text-sm font-semibold text-[#0F172A]">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="pt-4">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="px-8 py-4 bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold rounded-2xl shadow-xl active:scale-95 transition-all"
                                >
                                    Try KithHR Free →
                                </button>
                            </div>
                        </div>

                        {/* Visual Column */}
                        <div className="lg:col-span-7 relative">
                            {activeTab === 'clockin' && (
                                <div className="flex items-center justify-center gap-2 mb-3">
                                    <button
                                        onClick={() => setClockInView('sidebar')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${clockInView === 'sidebar'
                                            ? 'bg-[#0062D2] text-white shadow-md'
                                            : 'bg-[#FAF8F5] text-[#475569] hover:bg-[#F5F1EB] border border-[#E8E2D8]'
                                            }`}
                                    >
                                        1. Navigation Sidebar
                                    </button>
                                    <span className="text-[#94A3B8] text-xs font-semibold">➔</span>
                                    <button
                                        onClick={() => setClockInView('terminal')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${clockInView === 'terminal'
                                            ? 'bg-[#0062D2] text-white shadow-md'
                                            : 'bg-[#FAF8F5] text-[#475569] hover:bg-[#F5F1EB] border border-[#E8E2D8]'
                                            }`}
                                    >
                                        2. PIN Terminal Clock-In
                                    </button>
                                </div>
                            )}
                            <div
                                onClick={() => activeTab === 'clockin' && setClockInView(prev => prev === 'sidebar' ? 'terminal' : 'sidebar')}
                                className={`rounded-2xl overflow-hidden shadow-xl border border-[#E8E2D8] bg-[#FAF8F5] group ${activeTab === 'clockin' ? 'cursor-pointer hover:border-[#0062D2] transition-all' : ''}`}
                            >
                                <img
                                    src={current.image}
                                    alt={current.title}
                                    width="800"
                                    height="460"
                                    loading="lazy"
                                    className="w-full max-h-[460px] object-contain mx-auto transition-all duration-500 transform group-hover:scale-[1.01]"
                                />
                                {activeTab === 'clockin' && (
                                    <div className="bg-[#0F172A]/80 backdrop-blur-md px-4 py-2 text-center text-xs font-medium text-slate-200 flex items-center justify-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping"></span>
                                        <span>Click image or wait to toggle Kiosk launch workflow</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 6. KITHHR VS MANUAL TIMEKEEPING
const ComparisonSection: React.FC = () => {
    const navigate = useNavigate();
    const [mobileFilter, setMobileFilter] = useState<'all' | 'payroll' | 'attendance'>('all');

    const comparisonItems = [
        {
            id: 'clockin',
            category: 'attendance' as const,
            badge: 'Fraud & Time Theft',
            icon: ShieldCheck,
            feat: 'Clock-In & Identity Verification',
            oldTitle: 'Paper Sheets & Honour System',
            oldDesc: 'Handwritten sign-in sheets, unverified timestamps, high buddy-punching risk, and inflated rounding (+15 min/shift).',
            kithTitle: 'PIN Kiosk & GPS Verification',
            kithDesc: 'Dedicated tablet PIN Kiosk or GPS mobile check-in. Instant identity check, exact minute tracking, zero buddy punching.',
            stat: 'Stops 2–5% payroll leakage',
        },
        {
            id: 'rota',
            category: 'attendance' as const,
            badge: 'Shift Scheduling',
            icon: CalendarDays,
            feat: 'Shift Rota Planning & Visibility',
            oldTitle: 'WhatsApp Chaos & Paper Rotas',
            oldDesc: 'Frantic group chats, printed sheets pinned to breakroom walls, missed shift updates, and staff confusion.',
            kithTitle: 'Live Cloud Rota on Mobile',
            kithDesc: 'Visual cloud rota published in seconds. Staff see upcoming shifts instantly on their phones with clean cover requests.',
            stat: 'Zero missed shifts',
        },
        {
            id: 'radar',
            category: 'attendance' as const,
            badge: 'Site Operations',
            icon: Radio,
            feat: 'Real-Time Site Attendance',
            oldTitle: 'Zero Visibility Until Payday',
            oldDesc: 'Managers must call around or physically inspect sites to know who actually showed up on the floor or kitchen.',
            kithTitle: 'Live Site Radar & Headcount',
            kithDesc: 'Instant live view of who is clocked in, on break, or late across every location. Emergency roll-call ready in seconds.',
            stat: 'Real-time site visibility',
        },
        {
            id: 'breaks',
            category: 'payroll' as const,
            badge: 'Hours & Deductions',
            icon: Clock,
            feat: 'Break Deductions & Overtime',
            oldTitle: 'Manual Mental Math & Errors',
            oldDesc: 'Managers spend hours calculating 20m/30m unpaid break deductions and overtime tiers with frequent calculation disputes.',
            kithTitle: 'Automated Precise Deductions',
            kithDesc: 'Auto-applies UK statutory break rules and overtime policies to the exact second. Zero manual math required.',
            stat: 'Zero math mistakes',
        },
        {
            id: 'holiday',
            category: 'payroll' as const,
            badge: 'UK Compliance',
            icon: Plane,
            feat: '12.07% Holiday & Statutory Leave',
            oldTitle: 'Broken Excel Formulas & Disputes',
            oldDesc: 'Complex spreadsheets break frequently, risking underpaid holiday pay claims, tribunal disputes, and HMRC penalties.',
            kithTitle: 'Statutory 12.07% Accrual Engine',
            kithDesc: 'Automates irregular hours leave accruals, tracks live balances for staff, and stores 1-tap approvals for HMRC audits.',
            stat: '100% HMRC compliant',
        },
        {
            id: 'payroll',
            category: 'payroll' as const,
            badge: 'Payroll Prep',
            icon: FileCheck,
            feat: 'Payroll Reports & Accountant Export',
            oldTitle: 'Sunday Night Re-Entry',
            oldDesc: '4–6 hours spent deciphering messy handwriting, fixing disputes, and re-typing numbers into Xero or Sage every month.',
            kithTitle: '1-Click PDF & CSV Export',
            kithDesc: 'Download verified gross hours, deductions, and pay summaries formatted for your accountant in under 60 seconds.',
            stat: 'Saves 4.5 hrs/week',
        },
    ];

    const filteredItems = comparisonItems.filter(item => {
        if (mobileFilter === 'all') return true;
        return item.category === mobileFilter;
    });

    return (
        <AnimatedSection className="bg-[#F5F1EB]/50 py-16 md:py-24" id="comparison">
            <div className="container mx-auto px-4 md:px-6">
                <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
                    <span className="px-3.5 py-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-xs font-black uppercase tracking-widest inline-block mb-3">
                        Paper vs Digital Time Tracking
                    </span>
                    <h2 className="text-3xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        KithHR vs Manual Timekeeping
                    </h2>
                    <p className="text-base md:text-lg text-[#475569] font-medium leading-relaxed">
                        Why UK businesses move away from manual paper timesheets and spreadsheets.
                    </p>

                    {/* Executive Impact Metrics Strip */}
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto text-left">
                        <div className="bg-white p-4 rounded-2xl border border-[#E8E2D8] shadow-xs flex items-center gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#005BB5] flex items-center justify-center shrink-0 font-black text-lg">
                                ⏱️
                            </div>
                            <div>
                                <div className="text-sm font-black text-[#0F172A]">4.5 Hrs / Wk Saved</div>
                                <div className="text-xs text-[#64748B]">Zero Sunday timesheet re-entry</div>
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl border border-[#E8E2D8] shadow-xs flex items-center gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 font-black text-lg">
                                🛡️
                            </div>
                            <div>
                                <div className="text-sm font-black text-[#0F172A]">Zero Buddy Punching</div>
                                <div className="text-xs text-[#64748B]">Verified PIN kiosk &amp; GPS</div>
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl border border-[#E8E2D8] shadow-xs flex items-center gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 font-black text-lg">
                                ⚖️
                            </div>
                            <div>
                                <div className="text-sm font-black text-[#0F172A]">100% HMRC Audit Proof</div>
                                <div className="text-xs text-[#64748B]">Automatic 12.07% holiday rules</div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Category Filter Pills (Visible on Mobile Only) */}
                    <div className="mt-6 md:hidden flex justify-center">
                        <div className="bg-[#E8E2D8]/60 p-1 rounded-2xl flex items-center gap-1 border border-[#E8E2D8] max-w-full overflow-x-auto">
                            <button
                                onClick={() => setMobileFilter('all')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
                                    mobileFilter === 'all'
                                        ? 'bg-[#0062D2] text-white shadow-md'
                                        : 'text-[#475569] hover:text-[#0F172A]'
                                }`}
                            >
                                All 6 Differences
                            </button>
                            <button
                                onClick={() => setMobileFilter('payroll')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
                                    mobileFilter === 'payroll'
                                        ? 'bg-[#0062D2] text-white shadow-md'
                                        : 'text-[#475569] hover:text-[#0F172A]'
                                }`}
                            >
                                Payroll &amp; Hours
                            </button>
                            <button
                                onClick={() => setMobileFilter('attendance')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
                                    mobileFilter === 'attendance'
                                        ? 'bg-[#0062D2] text-white shadow-md'
                                        : 'text-[#475569] hover:text-[#0F172A]'
                                }`}
                            >
                                Attendance &amp; Rota
                            </button>
                        </div>
                    </div>
                </div>

                {/* MOBILE VIEW: Touch-Friendly Vertical Cards Stack (No horizontal scrolling) */}
                <div className="md:hidden space-y-4 max-w-lg mx-auto">
                    {filteredItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={item.id}
                                className="bg-white rounded-2xl p-5 border border-[#E8E2D8] shadow-md transition-all space-y-3.5"
                            >
                                {/* Card Header */}
                                <div className="flex items-center justify-between gap-2 border-b border-[#F1ECE4] pb-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#005BB5] flex items-center justify-center shrink-0">
                                            <Icon size={16} />
                                        </div>
                                        <div>
                                            <div className="text-[11px] font-black text-[#64748B] uppercase tracking-wider">
                                                {item.badge}
                                            </div>
                                            <h3 className="text-sm font-black text-[#0F172A] leading-tight">
                                                {item.feat}
                                            </h3>
                                        </div>
                                    </div>
                                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200 shrink-0">
                                        {item.stat}
                                    </span>
                                </div>

                                {/* Comparison Blocks: Pain vs Relief */}
                                <div className="space-y-2.5">
                                    {/* Manual / Paper (Old Way) */}
                                    <div className="p-3 bg-red-50/60 rounded-xl border border-red-100 text-left">
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 mb-1">
                                            <X size={14} strokeWidth={3} className="text-red-500 shrink-0" />
                                            <span>Manual / Paper &amp; Spreadsheets</span>
                                        </div>
                                        <div className="text-xs font-bold text-[#0F172A] mb-0.5">
                                            {item.oldTitle}
                                        </div>
                                        <p className="text-xs text-[#64748B] leading-relaxed">
                                            {item.oldDesc}
                                        </p>
                                    </div>

                                    {/* KithHR (New Way) */}
                                    <div className="p-3 bg-emerald-50/70 rounded-xl border border-emerald-200/70 text-left">
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 mb-1">
                                            <Check size={14} strokeWidth={3} className="text-emerald-600 shrink-0" />
                                            <span>With KithHR (Free for 15)</span>
                                        </div>
                                        <div className="text-xs font-bold text-[#0F172A] mb-0.5">
                                            {item.kithTitle}
                                        </div>
                                        <p className="text-xs text-[#334155] leading-relaxed">
                                            {item.kithDesc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* DESKTOP VIEW: Sleek Side-by-Side Comparison Table (Hidden on Mobile) */}
                <div className="hidden md:block max-w-4xl mx-auto bg-white rounded-[2.5rem] shadow-xl border border-[#E8E2D8] overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <caption className="sr-only">Comparison between Manual Timekeeping and KithHR</caption>
                        <thead>
                            <tr className="bg-[#FAF8F5] border-b border-[#E8E2D8]">
                                <th className="p-6 lg:p-7 text-xs font-black text-[#0F172A] uppercase tracking-widest w-1/3">
                                    Workflow Capability
                                </th>
                                <th className="p-6 lg:p-7 text-left text-blue-900 font-black uppercase tracking-widest bg-blue-50/70 w-1/3 border-x border-blue-100">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-md bg-[#0062D2] text-white flex items-center justify-center text-xs font-black">
                                            ✓
                                        </div>
                                        <span>With KithHR</span>
                                    </div>
                                </th>
                                <th className="p-6 lg:p-7 text-left text-[#64748B] font-black uppercase tracking-widest w-1/3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-md bg-red-100 text-red-600 flex items-center justify-center text-xs font-black">
                                            ✕
                                        </div>
                                        <span>Manual / Paper &amp; Excel</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="text-[#334155] divide-y divide-[#E8E2D8]">
                            {comparisonItems.map((row, i) => {
                                const Icon = row.icon;
                                return (
                                    <tr key={i} className="hover:bg-[#FAF8F5]/80 transition">
                                        <td className="p-6 lg:p-7">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-[#F5F1EB] text-[#005BB5] flex items-center justify-center shrink-0">
                                                    <Icon size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-black text-[#64748B] uppercase tracking-wider">
                                                        {row.badge}
                                                    </div>
                                                    <div className="font-bold text-[#0F172A] text-sm lg:text-base">
                                                        {row.feat}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-6 lg:p-7 bg-blue-50/40 border-x border-blue-100/70">
                                            <div className="space-y-1">
                                                <div className="inline-flex items-center gap-1.5 font-black text-blue-900 text-sm">
                                                    <Check size={16} strokeWidth={3} className="text-emerald-600 shrink-0" />
                                                    <span>{row.kithTitle}</span>
                                                </div>
                                                <p className="text-xs text-[#334155] leading-relaxed">
                                                    {row.kithDesc}
                                                </p>
                                                <span className="inline-block mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                                    {row.stat}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-6 lg:p-7">
                                            <div className="space-y-1">
                                                <div className="inline-flex items-center gap-1.5 font-bold text-red-700 text-sm">
                                                    <X size={16} strokeWidth={3} className="text-red-500 shrink-0" />
                                                    <span>{row.oldTitle}</span>
                                                </div>
                                                <p className="text-xs text-[#64748B] leading-relaxed">
                                                    {row.oldDesc}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Bottom Call to Action & In-Depth Guide Teaser */}
                <div className="mt-8 max-w-4xl mx-auto bg-white rounded-3xl p-6 md:p-8 border border-[#E8E2D8] shadow-lg flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
                    <div className="space-y-1">
                        <div className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                            <span>📖 Deep-Dive Resource</span>
                        </div>
                        <h3 className="text-base md:text-lg font-black text-[#0F172A]">
                            Want the full math on paper vs digital costs?
                        </h3>
                        <p className="text-xs md:text-sm text-[#64748B]">
                            Read how UK businesses save £180+/month per site in payroll errors and admin time.
                        </p>
                        <div>
                            <button
                                onClick={() => navigate('/resources/paper-timesheets-vs-digital-time-tracking')}
                                className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline pt-1"
                            >
                                <span>Read "Paper Timesheets vs Digital Time Tracking Guide"</span>
                                <ArrowRight size={13} />
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full sm:w-auto shrink-0 bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-3.5 rounded-2xl shadow-lg hover:scale-105 active:scale-95 transition-all text-sm flex items-center justify-center gap-2"
                    >
                        <span>Start Free Forever (15 Staff)</span>
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 7. PRACTICAL AI SECTION (REDUCED HYPE)
const DehypedAISection: React.FC = () => {
    return (
        <AnimatedSection className="bg-white py-24 border-t border-[#E8E2D8]" id="ai-automation">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-50 text-purple-700 text-xs font-black uppercase tracking-widest mb-4 border border-purple-200">
                        Administrative Helper
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        Let KithHR handle the repetitive work
                    </h2>
                    <p className="text-lg text-[#475569] font-medium leading-relaxed">
                        KithHR helps you find workforce information faster and highlights useful attendance patterns automatically.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    <div className="bg-[#FAF8F5] p-8 rounded-[2rem] border border-[#E8E2D8] shadow-xs flex flex-col justify-between">
                        <div>
                            <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mb-6">
                                <Brain size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                                Highlight Attendance Patterns
                            </h3>
                            <p className="text-[#475569] text-sm leading-relaxed">
                                Spot punctuality trends and recurring late clock-ins automatically before they affect site operations.
                            </p>
                        </div>
                    </div>

                    <div className="bg-[#FAF8F5] p-8 rounded-[2rem] border border-[#E8E2D8] shadow-xs flex flex-col justify-between">
                        <div>
                            <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6">
                                <AlertTriangle size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                                Prevent Schedule Conflicts
                            </h3>
                            <p className="text-[#475569] text-sm leading-relaxed">
                                Receive instant warnings if an employee is double-booked across different sites or overlapping rotas.
                            </p>
                        </div>
                    </div>

                    <div className="bg-[#FAF8F5] p-8 rounded-[2rem] border border-[#E8E2D8] shadow-xs flex flex-col justify-between">
                        <div>
                            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-6">
                                <FileCheck size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                                Simplify Administrative Math
                            </h3>
                            <p className="text-[#475569] text-sm leading-relaxed">
                                Automatically calculate total working hours, scheduled break deductions, and statutory holiday leave.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 text-center">
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-50 border border-purple-200 text-purple-900 text-xs font-semibold shadow-xs">
                        <span>💻 <strong>Manager Tip:</strong> Full AI Assistant &amp; Attendance Analytics run on the Web Dashboard (kithhr.co.uk). Staff can clock in using the mobile app or tablet kiosk.</span>
                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 8. SECURITY AND PRIVACY SECTION
const SecurityPrivacySection: React.FC = () => {
    return (
        <AnimatedSection className="bg-[#FAF8F5] text-[#0F172A] py-24 border-t border-[#E8E2D8]" id="security">
            <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-800 text-xs font-black uppercase tracking-widest mb-6 border border-blue-200">
                        <ShieldCheck size={16} /> Data Security &amp; UK Compliance
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-6">
                        UK Hosted &amp; Built for GDPR Compliance
                    </h2>
                    <p className="text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto mb-12 font-medium">
                        Specific, transparent security information to ensure your business data is protected.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                        <div className="p-8 rounded-3xl bg-white border border-[#E8E2D8] shadow-md hover:shadow-xl transition-shadow">
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3 flex items-center gap-2">
                                <Globe className="text-[#005BB5]" size={20} /> UK Server Infrastructure
                            </h3>
                            <p className="text-sm text-[#475569] leading-relaxed font-normal">
                                All employee profiles, timesheets, and attendance logs are securely hosted on servers located in the UK in full compliance with UK GDPR standards.
                            </p>
                        </div>

                        <div className="p-8 rounded-3xl bg-white border border-[#E8E2D8] shadow-md hover:shadow-xl transition-shadow">
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3 flex items-center gap-2">
                                <Lock className="text-emerald-600" size={20} /> Local PIN Verification
                            </h3>
                            <p className="text-sm text-[#475569] leading-relaxed font-normal">
                                Kiosk sign-ins use secure 4-digit PIN verification processed locally on your kiosk terminal. No raw photos or biometric scans are stored in cloud databases.
                            </p>
                        </div>

                        <div className="p-8 rounded-3xl bg-white border border-[#E8E2D8] shadow-md hover:shadow-xl transition-shadow">
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3 flex items-center gap-2">
                                <ShieldCheck className="text-purple-600" size={20} /> UK GDPR & Privacy Guidelines
                            </h3>
                            <p className="text-sm text-[#475569] leading-relaxed font-normal">
                                KithHR is designed with UK GDPR requirements in mind and supports compliance with upcoming UK employment-rights changes.
                            </p>
                        </div>

                        <div className="p-8 rounded-3xl bg-white border border-[#E8E2D8] shadow-md hover:shadow-xl transition-shadow">
                            <h3 className="text-xl font-bold text-[#0F172A] mb-3 flex items-center gap-2">
                                <FileCheck className="text-amber-600" size={20} /> Digital Audit Trail
                            </h3>
                            <p className="text-sm text-[#475569] leading-relaxed font-normal">
                                Every timesheet edit, shift approval, and clock-in log is preserved with an immutable digital audit history for payroll compliance.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 9. ALL FEATURES MATRIX
const AllFeaturesMatrix: React.FC = () => {
    const modules = [
        {
            title: "Time Clock & Verification",
            items: ["PIN-based tablet kiosk", "Offline clock-in resilience", "GPS site geofencing", "Local device verification", "PWA home screen install"]
        },
        {
            title: "Scheduling & Shift Rotas",
            items: ["Drag-and-drop rota builder", "Double-booking alerts", "Rotating shift patterns", "Absence & leave logging", "Google & Apple iCal sync"]
        },
        {
            title: "Workforce Intelligence",
            items: ["Live site map radar", "Missed clock-in alerts", "Attendance pattern insights", "Probation milestone tracking", "Site department filters"]
        },
        {
            title: "Payroll & Compliance",
            items: ["Gross pay calculator", "Break deduction rules", "1-click PDF/CSV exports", "Supports statutory holiday calculations", "UK GDPR audit trail"]
        }
    ];

    return (
        <AnimatedSection className="bg-white py-24 border-t border-[#E8E2D8]" id="all-features">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        All Features
                    </h2>
                    <p className="text-lg text-[#475569] font-medium">
                        A complete breakdown of capabilities included in KithHR.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                    {modules.map(mod => (
                        <div key={mod.title} className="bg-[#FAF8F5] p-8 rounded-3xl border border-[#E8E2D8] shadow-xs">
                            <h3 className="text-xl font-extrabold text-[#0F172A] mb-6 border-b border-[#E8E2D8] pb-3">
                                {mod.title}
                            </h3>
                            <ul className="space-y-3">
                                {mod.items.map((item, idx) => (
                                    <li key={idx} className="flex items-center gap-2.5 text-sm font-semibold text-[#334155]">
                                        <Check size={16} className="text-teal-600 shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </AnimatedSection>
    );
};

// 10. AVAILABLE NOW VS COMING SOON ROADMAP
const RoadmapSection: React.FC = () => {
    return (
        <AnimatedSection className="bg-[#FAF8F5] py-24 border-t border-[#E8E2D8]" id="roadmap">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-xs font-black uppercase tracking-widest mb-4">
                        Product Roadmap
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                        Available Now vs. Coming Soon
                    </h2>
                    <p className="text-lg text-[#475569] font-medium">
                        Clear separation between current live functionality and upcoming features.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {/* AVAILABLE NOW */}
                    <div className="bg-white p-8 rounded-[2.5rem] border border-emerald-200 shadow-md">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-700 text-white text-xs font-black uppercase tracking-widest mb-6">
                            <CheckCircle2 size={14} /> Available Now
                        </div>
                        <ul className="space-y-4">
                            {[
                                "Native Android App (Live on Google Play Store)",
                                "Web-based PIN Kiosk Terminal (runs on any browser / tablet)",
                                "Drag-and-Drop Shift Rota Calendar",
                                "Real-time Manager Dashboard & Live Attendance Map",
                                "Automated Gross Hours & Scheduled Break Deductions",
                                "GPS Geofencing Location Verification",
                                "1-Click PDF and CSV Timesheet Exports",
                                "iCal Calendar Sync Feeds for Google & Apple"
                            ].map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                    <div className="p-1 bg-emerald-100 text-emerald-700 rounded-full shrink-0 mt-0.5">
                                        <Check size={14} strokeWidth={3} />
                                    </div>
                                    <span className="text-sm font-semibold text-[#0F172A]">
                                        {item.includes("Google Play Store") ? (
                                            <a href="https://play.google.com/store/apps/details?id=com.kithhr.app" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline font-bold flex items-center gap-1.5">
                                                {item} ↗
                                            </a>
                                        ) : item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* COMING SOON */}
                    <div className="bg-white p-8 rounded-[2.5rem] border border-amber-200 shadow-md">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-700 text-white text-xs font-black uppercase tracking-widest mb-6">
                            <Clock size={14} /> Coming Soon
                        </div>
                        <ul className="space-y-4">
                            {[
                                "KithHR WhatsApp Frontline Assistant (Private Beta: Rota & Leave Queries)",
                                "Native iOS App Store Download (In Review)",
                                "Pre-Printed Acrylic NFC Desk Plaques",
                                "Direct 1-Click Xero & Sage Payroll API Integration",
                                "Automated Employee Shift Swap Request Approval",
                                "Custom Multi-Department Permission Roles"
                            ].map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                    <div className="p-1 bg-amber-100 text-amber-700 rounded-full shrink-0 mt-0.5">
                                        <Clock size={14} strokeWidth={3} />
                                    </div>
                                    <span className="text-sm font-semibold text-[#0F172A]">
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

// 11. PRICING SECTION
const PricingSection: React.FC = () => {
    const navigate = useNavigate();
    const [employeeCount, setEmployeeCount] = useState<number>(15);

    // Calculate effective rate per user based on employee count
    const getRateInfo = (count: number) => {
        if (count <= 15) return { rate: 0, label: "Free Forever", tierName: "🟢 Free Tier", breatheHrCost: 579 };
        if (count <= 50) return { rate: 3.49, label: "£3.49 / user / mo", tierName: "🔵 Starter", breatheHrCost: 579 };
        if (count <= 120) return { rate: 2.99, label: "£2.99 / user / mo", tierName: "🟣 Growth", breatheHrCost: 579 };
        if (count <= 200) return { rate: 2.25, label: "£2.25 / user / mo", tierName: "🟡 Scale", breatheHrCost: 579 };
        if (count <= 500) return { rate: 1.75, label: "£1.75 / user / mo", tierName: "🟠 Enterprise", breatheHrCost: 890 };
        return { rate: 1.50, label: "Custom SLA", tierName: "👑 Custom Enterprise", breatheHrCost: 1200 };
    };

    const currentRateInfo = getRateInfo(employeeCount);
    const totalMonthlyCost = employeeCount <= 15 ? 0 : Math.round(employeeCount * currentRateInfo.rate * 100) / 100;
    const breatheMonthlyEstimate = currentRateInfo.breatheHrCost;
    const monthlySavings = Math.max(0, breatheMonthlyEstimate - totalMonthlyCost);
    const annualSavings = monthlySavings * 12;

    const tiers = [
        {
            badge: "🟢 Free Forever",
            range: "1 – 15 Employees",
            price: "£0",
            period: "Free Forever",
            desc: "Full PRO features for small UK teams & startups.",
            highlight: false,
            color: "border-green-500/30 text-green-500",
            btnText: "Get Started Free"
        },
        {
            badge: "🔵 Starter",
            range: "16 – 50 Employees",
            price: "£3.49",
            period: "/ user / month",
            desc: "For growing teams that need GPS & live shift maps.",
            highlight: false,
            color: "border-blue-500/30 text-blue-400",
            btnText: "Start Free Now"
        },
        {
            badge: "🟣 Growth • Popular",
            range: "51 – 120 Employees",
            price: "£2.99",
            period: "/ user / month",
            desc: "Optimal value for mid-size businesses & agencies.",
            highlight: true,
            color: "border-purple-500 text-purple-400",
            btnText: "Start Free Now"
        },
        {
            badge: "🟡 Scale",
            range: "121 – 200 Employees",
            price: "£2.25",
            period: "/ user / month",
            desc: "For larger workforces scaling across sites.",
            highlight: false,
            color: "border-amber-500/30 text-amber-400",
            btnText: "Start Free Now"
        },
        {
            badge: "🟠 Enterprise",
            range: "201 – 500+ Employees",
            price: "£1.75",
            period: "/ user / month",
            desc: "Unbeatable high-volume pricing with 0 contracts.",
            highlight: false,
            color: "border-orange-500/30 text-orange-400",
            btnText: "Start Free Now"
        }
    ];

    return (
        <AnimatedSection className="bg-[#FAF8F5] py-24" id="pricing">
            <div className="container mx-auto px-6 max-w-6xl">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-800 text-xs font-black uppercase tracking-widest border border-blue-200">
                        <Sparkles size={14} /> PRO ALL-IN-ONE • 0 CONTRACTS
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight">
                        Every Premium Feature Included.<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-700 via-blue-600 to-indigo-700">
                            Zero Contracts. No Add-on Fees.
                        </span>
                    </h2>
                    <p className="text-lg text-[#475569] font-medium">
                        1 to 15 employees are <strong className="text-emerald-600 font-bold">Free Forever</strong> with full PRO access. Scale up or down anytime with zero minimum commitment.
                    </p>
                </div>

                {/* Interactive Calculator Box */}
                <div className="bg-white rounded-3xl sm:rounded-[2.5rem] p-5 sm:p-8 md:p-12 border border-[#E8E2D8] shadow-xl mb-16 max-w-4xl mx-auto">
                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-8 border-b border-slate-100 pb-6 sm:pb-8">
                        <div>
                            <span className="text-xs font-black uppercase text-teal-700 tracking-widest">Interactive Calculator</span>
                            <h3 className="text-xl sm:text-2xl font-black text-[#0F172A] mt-1">Calculate Your Monthly Investment</h3>
                            <p className="text-xs sm:text-sm text-[#64748B] mt-0.5">Transparent UK pricing · 0 hidden setup fees</p>
                        </div>

                        {/* Stepper Controller */}
                        <div className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-3 bg-[#FAF8F5] p-2 sm:p-2.5 rounded-2xl border border-[#E8E2D8]">
                            <span className="text-xs font-bold text-[#475569] pl-1">Employees:</span>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setEmployeeCount(c => Math.max(1, c - 1))}
                                    aria-label="Decrease employee count"
                                    className="w-8 h-8 rounded-xl bg-white hover:bg-slate-100 active:scale-95 border border-[#E8E2D8] flex items-center justify-center text-[#0F172A] font-bold text-sm shadow-2xs transition cursor-pointer"
                                >
                                    <Minus size={14} />
                                </button>
                                <input
                                    id="pricing-employee-count"
                                    aria-label="Number of employees for pricing calculation"
                                    type="number"
                                    min="1"
                                    max="500"
                                    value={employeeCount}
                                    onChange={(e) => setEmployeeCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                                    className="w-16 sm:w-20 px-2 py-1 bg-white text-[#0F172A] font-black text-base sm:text-lg rounded-xl border border-[#E8E2D8] text-center focus:ring-2 focus:ring-[#0062D2] outline-none"
                                />
                                <button
                                    onClick={() => setEmployeeCount(c => Math.min(500, c + 1))}
                                    aria-label="Increase employee count"
                                    className="w-8 h-8 rounded-xl bg-white hover:bg-slate-100 active:scale-95 border border-[#E8E2D8] flex items-center justify-center text-[#0F172A] font-bold text-sm shadow-2xs transition cursor-pointer"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Quick-Tap Presets (Mobile & Desktop Friendly) */}
                    <div className="mb-6">
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-2">
                            Quick Select Team Size:
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { count: 5, label: "5 Staff" },
                                { count: 10, label: "10 Staff" },
                                { count: 15, label: "15 Staff (Free)", highlight: true },
                                { count: 25, label: "25 Staff" },
                                { count: 50, label: "50 Staff" },
                                { count: 100, label: "100 Staff" },
                                { count: 250, label: "250 Staff" }
                            ].map((preset) => {
                                const isSelected = employeeCount === preset.count;
                                return (
                                    <button
                                        key={preset.count}
                                        onClick={() => setEmployeeCount(preset.count)}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer ${
                                            isSelected
                                                ? preset.highlight
                                                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20 ring-2 ring-emerald-500'
                                                    : 'bg-[#0F172A] text-white shadow-md shadow-slate-900/20 ring-2 ring-[#0062D2]'
                                                : preset.highlight
                                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100'
                                                    : 'bg-[#FAF8F5] text-[#475569] border border-[#E8E2D8] hover:bg-white hover:text-[#0F172A]'
                                        }`}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Touch Slider */}
                    <div className="space-y-3 mb-8">
                        <label htmlFor="pricing-employee-slider" className="sr-only">
                            Employee Count Slider
                        </label>
                        <input
                            id="pricing-employee-slider"
                            aria-label="Employee count slider for pricing calculation"
                            type="range"
                            min="1"
                            max="500"
                            value={employeeCount}
                            onChange={(e) => setEmployeeCount(parseInt(e.target.value))}
                            className="w-full h-3 sm:h-4 bg-[#E8E2D8] rounded-full appearance-none cursor-pointer accent-[#0062D2]"
                        />
                        {/* Desktop Detailed Labels */}
                        <div className="hidden sm:flex justify-between text-xs font-black text-[#64748B] uppercase tracking-widest px-1">
                            <span>1 (£0)</span>
                            <span className="text-emerald-600 font-bold">15 (Free Limit)</span>
                            <span>50 (£3.49)</span>
                            <span>120 (£2.99)</span>
                            <span>200 (£2.25)</span>
                            <span>500 (£1.75)</span>
                        </div>
                        {/* Mobile Clean Labels */}
                        <div className="flex sm:hidden justify-between text-[11px] font-black text-[#64748B] px-1 font-mono">
                            <span>1</span>
                            <span className="text-emerald-600 font-bold">15 (Free)</span>
                            <span>50</span>
                            <span>120</span>
                            <span>500</span>
                        </div>
                    </div>

                    {/* Cost Breakdown Display */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-6 mb-6">
                        <div className="bg-[#FAF8F5] p-4 sm:p-6 rounded-2xl border border-[#E8E2D8] text-center">
                            <div className="text-[11px] sm:text-xs font-black uppercase text-[#64748B] mb-1">Active Tier</div>
                            <div className="text-lg sm:text-xl font-black text-teal-700">{currentRateInfo.tierName}</div>
                            <div className="text-xs sm:text-sm font-bold text-[#475569] mt-0.5">{currentRateInfo.label}</div>
                        </div>

                        <div className="bg-blue-50 p-4 sm:p-6 rounded-2xl border border-blue-200 text-center relative overflow-hidden">
                            {employeeCount <= 15 && (
                                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-black uppercase tracking-wider">
                                    Free Forever
                                </span>
                            )}
                            <div className="text-[11px] sm:text-xs font-black uppercase text-blue-700 mb-1">Your KithHR Total</div>
                            <div className="text-2xl sm:text-3xl font-black text-[#0F172A]">
                                {totalMonthlyCost === 0 ? "£0 / mo" : `£${totalMonthlyCost.toFixed(2)} / mo`}
                            </div>
                            <div className="text-[11px] sm:text-xs font-bold text-emerald-700 mt-1">0 Contracts • Cancel Anytime</div>
                        </div>

                        <div className="bg-emerald-50 p-4 sm:p-6 rounded-2xl border border-emerald-200 text-center">
                            <div className="text-[11px] sm:text-xs font-black uppercase text-emerald-700 mb-1">Estimated Annual Savings</div>
                            <div className="text-2xl sm:text-3xl font-black text-emerald-700">
                                {annualSavings > 0 ? `£${Math.round(annualSavings).toLocaleString()} / yr` : "100% Free"}
                            </div>
                            <div className="text-[11px] sm:text-xs font-bold text-[#64748B] mt-1">vs Breathe HR base fees</div>
                        </div>
                    </div>

                    {/* Mobile-Friendly Direct Action Button */}
                    <div className="pt-2">
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold py-3.5 sm:py-4 px-6 rounded-2xl shadow-lg shadow-blue-500/20 active:scale-95 transition-all text-sm sm:text-base flex items-center justify-center gap-2 cursor-pointer"
                        >
                            {employeeCount <= 15 
                                ? "Start 100% Free Forever (Up to 15 Staff) →"
                                : `Start 14-Day Free Trial for ${employeeCount} Staff →`
                            }
                        </button>
                        <p className="text-center text-[11px] text-[#64748B] mt-2 font-medium">
                            ✓ No credit card required · Instant 2-minute setup · Full PRO feature access
                        </p>
                    </div>
                </div>

                {/* 5 Tier Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
                    {tiers.map((t, idx) => (
                        <div 
                            key={idx}
                            className={`rounded-3xl p-6 flex flex-col justify-between border transition-all ${t.highlight ? 'bg-[#0F172A] text-white border-purple-500 ring-2 ring-purple-500/50 shadow-xl scale-[1.02]' : 'bg-white border-[#E8E2D8] shadow-sm hover:shadow-md'}`}
                        >
                            <div>
                                <div className={`text-[10px] font-black uppercase tracking-wider mb-2 ${t.color}`}>
                                    {t.badge}
                                </div>
                                <h4 className={`text-lg font-black mb-1 ${t.highlight ? 'text-white' : 'text-[#0F172A]'}`}>{t.range}</h4>
                                <div className="my-3">
                                    <span className={`text-3xl font-black ${t.highlight ? 'text-white' : 'text-[#0F172A]'}`}>{t.price}</span>
                                    <span className={`text-[11px] font-bold block mt-0.5 ${t.highlight ? 'text-slate-400' : 'text-[#64748B]'}`}>{t.period}</span>
                                </div>
                                <p className={`text-xs mb-6 leading-relaxed ${t.highlight ? 'text-slate-300' : 'text-[#475569]'}`}>
                                    {t.desc}
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/login')}
                                className={`w-full py-3 text-xs font-black rounded-xl transition ${t.highlight ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg' : 'bg-[#FAF8F5] hover:bg-[#F5F1EB] text-[#0F172A] border border-[#E8E2D8]'}`}
                            >
                                {t.btnText}
                            </button>
                        </div>
                    ))}
                </div>

                {/* All Features Included Footer Banner */}
                <div className="bg-[#0F172A] rounded-3xl p-8 border border-slate-800 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
                    <div className="space-y-2 text-center md:text-left">
                        <h4 className="text-xl font-black tracking-tight text-white">Included in EVERY tier at no extra cost:</h4>
                        <p className="text-xs text-slate-300">
                            GPS Geofencing • Live Maps Radar • Mobile &amp; Tablet Kiosk • AI Rotas • HMRC-Style Gross Pay Drafts • Day-1 SSP • Xero Integration
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/login')}
                        className="px-8 py-4 bg-[#0062D2] hover:bg-[#004CB5] text-white font-black text-sm rounded-2xl shadow-xl active:scale-95 transition-all shrink-0 cursor-pointer"
                    >
                        Create Free Account
                    </button>
                </div>
            </div>
        </AnimatedSection>
    );
};

// MAIN LANDING COMPONENT
const Landing: React.FC = () => {
    const navigate = useNavigate();
    const [localModal, setLocalModal] = useState<{ title: string; content: React.ReactNode } | null>(null);

    const handleFooterNav = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.location.href = `/#${id}`;
        }
    };

    const faqData = [
        {
            question: "How does KithHR PIN clocking work?",
            answer: "Employees clock in or out in seconds using a secure 4-digit PIN on any phone, tablet, or web browser. GPS location or NFC verification can be enabled for site verification."
        },
        {
            question: "Is KithHR free to use?",
            answer: "Yes! KithHR offers a free Essential plan for up to 15 employees with clock-in/out and shift scheduling. Pro features including live site map tracking and geofence verification start at £3.49/user/month (or £0 for teams up to 15)."
        },
        {
            question: "Is KithHR ready for upcoming UK employment-rights changes?",
            answer: "Yes, KithHR is built with 2026 UK employment-rights changes in mind. The platform helps prepare accurate attendance and payroll information for your accountant or payroll provider while supporting UK GDPR data privacy principles."
        },
        {
            question: "What hardware do I need to buy to run KithHR?",
            answer: "Zero hardware costs. KithHR is a hardware-agnostic web application. You can run the PIN kiosk on any standard Apple iPad, Android tablet, smartphone, or desktop computer."
        },
        {
            question: "Can I export timesheets for payroll or import into Xero?",
            answer: "Yes! KithHR supports direct 1-click sync with Xero, as well as CSV and PDF exports pre-formatted for sharing directly with your accountant or payroll software."
        },
        {
            question: "What happens if the internet connection or Wi-Fi drops on site?",
            answer: "KithHR includes offline queuing resilience. Employees can still enter their 4-digit PIN on the tablet kiosk even when site Wi-Fi drops out. Clock-in timestamps cache locally on the device and sync automatically once connectivity restores."
        },
        {
            question: "Is KithHR compliant with UK GDPR and hosted in the UK?",
            answer: "Yes, KithHR is a UK platform designed with UK GDPR requirements in mind. All data is securely hosted on servers located in the UK, with kiosk verification processed locally on your tablet terminal."
        },
        {
            question: "Can I sync shifts and rotas with my personal phone calendar?",
            answer: "Yes! KithHR provides secure iCal sync feeds so managers and employees can subscribe to their shifts directly in Google Calendar, Apple Calendar, or Outlook."
        },
        {
            question: "Is KithHR free for small teams?",
            answer: "Yes! Teams of 1 to 15 employees receive 100% Free Forever access to all KithHR Pro features with zero credit card required. There is no trial expiry date and no hidden feature limits for small teams."
        },
        {
            question: "What is your refund and cancellation policy?",
            answer: "Teams of 1 to 15 employees are 100% Free Forever. For larger workforces (16+ employees), subscriptions operate on a rolling month-to-month basis with zero contracts. You can cancel or change seat capacity at any time with one click in your subscription settings."
        },
        {
            question: "Do admin users or managers get charged extra in KithHR?",
            answer: "No. Unlike platforms like Deputy where managers are billed as full paid seats or charged premium surcharges, KithHR includes admin and manager accounts at zero extra cost. Teams up to 15 staff pay £0 forever, while larger teams pay a flat £3.49/user/month with no hidden management fees."
        },
        {
            question: "How does KithHR pricing compare to Connecteam or Clockify for UK businesses?",
            answer: "Connecteam charges in US Dollars ($USD) with mandatory base fees starting at $29/month per hub, while Clockify limits free tiers and bills in USD. KithHR is engineered specifically for UK businesses with transparent GBP (£) pricing, zero base fees, free full access for up to 15 staff, and statutory 12.07% UK holiday accrual built-in."
        }
    ];

    return (
        <div className="min-h-screen bg-[#FAF8F5] text-[#0F172A] font-sans selection:bg-apple-blue-light selection:text-white overflow-x-hidden">
            <SEO
                title="KithHR — All-in-One Frontline Staff App: Shifts, PIN Kiosk & Holiday Tracker"
                description="KithHR (formerly TeamPulse) is the UK's all-in-one frontline staff app: drag-and-drop rotas, tablet PIN clock-in, 12.07% holiday accrual, and WhatsApp assistant. Free for up to 15 staff."
            />

            <LandingHeader />

            <main>
                {/* 1. HERO SECTION */}
                <section className="relative pt-36 sm:pt-44 md:pt-52 pb-20 md:pb-28 overflow-hidden bg-gradient-to-b from-[#F5F1EB] via-[#FAF8F5] to-[#FAF8F5]" id="hero">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 overflow-hidden pointer-events-none">
                        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-teal-500/10 rounded-full blur-[120px]"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px]"></div>
                    </div>

                    <div className="container mx-auto px-6 relative z-20">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="flex flex-col items-center">
                                <a 
                                    href="https://play.google.com/store/apps/details?id=com.kithhr.app"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 shadow-xs mb-8 hover:scale-105 transition-all"
                                >
                                    <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                                    <span className="text-xs font-bold tracking-tight">📱 Official Android App Live on Google Play →</span>
                                </a>

                                <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 tracking-tight leading-[1.15] text-[#0F172A]">
                                    Manage your{' '}
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-700 via-blue-600 to-indigo-700">
                                        staff, shifts and timekeeping
                                    </span>{' '}
                                    — all in one app.
                                </h1>

                                <p className="text-lg md:text-xl text-[#475569] mb-10 max-w-2xl font-medium leading-relaxed">
                                    Reduce the time spent managing rotas, clock-ins and payroll reports.
                                </p>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md mx-auto z-30 relative">
                                    <button
                                        onClick={() => navigate('/login')}
                                        className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-4 rounded-2xl text-base shadow-xl shadow-blue-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                                    >
                                        Start Free <ArrowRight size={18} />
                                    </button>

                                    <button
                                        onClick={() => {
                                            document.getElementById('see-in-action')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="w-full sm:w-auto bg-white text-[#1E293B] font-bold px-8 py-4 rounded-2xl text-base border border-[#E2D8CC] hover:bg-[#F5F1EB] transition shadow-xs flex items-center justify-center"
                                    >
                                        See How It Works
                                    </button>
                                </div>

                                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-xs font-bold text-[#64748B]">
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.kithhr.app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#E2D8CC] text-[#334155] hover:scale-105 transition-all shadow-xs"
                                    >
                                        <img src="/assets/google-play-badge.svg" alt="Google Play" className="h-4 w-auto object-contain" />
                                        <span>Official App on Google Play ↗</span>
                                    </a>
                                    <div className="flex items-center gap-1.5 text-[#334155]">
                                        <Check size={14} className="text-emerald-600 font-extrabold" /> Free up to 15 employees
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[#334155]">
                                        <Check size={14} className="text-emerald-600 font-extrabold" /> No credit card required
                                    </div>
                                </div>
                            </div>

                            {/* 2. REAL PRODUCT VISUAL */}
                            <HeroMockups />
                        </div>
                    </div>
                </section>

                {/* 3. FOUR CORE BENEFITS */}
                <FourCoreBenefits />

                {/* 3.5 WHATSAPP FRONTLINE ASSISTANT SPOTLIGHT */}
                <WhatsAppAssistantSpotlight />

                {/* 4. HOW IT WORKS */}
                <AnimatedSection className="bg-[#F5F1EB]/50 py-24" id="how-it-works">
                    <div className="container mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                                How KithHR Works in 3 Simple Steps
                            </h2>
                            <p className="text-lg text-[#475569] font-medium">
                                Simple setup designed for busy business owners and managers.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto text-center">
                            <div className="bg-white p-8 rounded-3xl border border-[#E8E2D8] shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-[#0062D2] text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-black shadow-md">
                                    1
                                </div>
                                <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">Desktop Dashboard</h3>
                                <p className="text-sm text-[#475569] leading-relaxed">
                                    Set up your team, departments, and shift rotas in minutes from any web browser.
                                </p>
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-[#E8E2D8] shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-[#0062D2] text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-black shadow-md">
                                    2
                                </div>
                                <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">Zero-Friction Kiosk</h3>
                                <p className="text-sm text-[#475569] leading-relaxed">
                                    Open kithhr.co.uk on any iPad or tablet browser for instant PIN employee clock-in.
                                </p>
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-[#E8E2D8] shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-[#0062D2] text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-black shadow-md">
                                    3
                                </div>
                                <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">Automated Payroll</h3>
                                <p className="text-sm text-[#475569] leading-relaxed">
                                    Timesheets sync instantly, calculating gross pay and generating accountant-ready summaries.
                                </p>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* 5. SEE KITHHR IN ACTION */}
                <SeeKithHRInAction />

                {/* 6. KITHHR VS MANUAL TIMEKEEPING */}
                <ComparisonSection />

                {/* 7. AI & AUTOMATION (DE-HYPED) */}
                <DehypedAISection />

                {/* 8. SECURITY & GDPR TRANSPARENCY */}
                <SecurityPrivacySection />

                {/* 9. ALL FEATURES MATRIX */}
                <AllFeaturesMatrix />

                {/* 10. AVAILABLE NOW VS COMING SOON */}
                <RoadmapSection />

                {/* 11. PRICING */}
                <PricingSection />

                {/* 12. FAQ */}
                <AnimatedSection className="bg-white py-24 border-t border-[#E8E2D8]" id="faq">
                    <div className="container mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                                Frequently Asked Questions
                            </h2>
                            <p className="text-lg text-[#475569] font-medium">
                                Clear answers to common questions about KithHR.
                            </p>
                        </div>

                        <div className="max-w-3xl mx-auto divide-y divide-[#E8E2D8]">
                            {faqData.map((faq, idx) => (
                                <FaqItem key={idx} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </div>
                </AnimatedSection>

                {/* 12.5 INSIGHTS FOR UK EMPLOYERS */}
                <AnimatedSection className="bg-[#F5F1EB]/50 py-24 border-t border-[#E8E2D8]" id="resources">
                    <div className="container mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="px-3.5 py-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-xs font-black uppercase tracking-widest inline-block mb-3">
                                Insights for UK Employers
                            </span>
                            <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] tracking-tight mb-4">
                                Practical UK Workforce & Compliance Guides
                            </h2>
                            <p className="text-lg text-[#475569] font-medium">
                                Practical guides on time tracking, staff scheduling, UK labor rules, and pre-payroll automation.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
                            <div
                                onClick={() => navigate('/resources/uk-holiday-entitlement-irregular-hours')}
                                className="group bg-white rounded-3xl p-7 shadow-md hover:shadow-xl border border-[#E8E2D8] transition-all cursor-pointer flex flex-col justify-between hover:-translate-y-1"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="px-3 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-[11px] font-black uppercase tracking-wider">
                                            UK Statutory Compliance
                                        </span>
                                        <ShieldCheck size={20} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-[#0F172A] group-hover:text-blue-700 transition-colors leading-snug">
                                        UK Holiday Entitlement for Irregular-Hours Workers (12.07% Guide)
                                    </h3>
                                    <p className="text-sm text-[#475569] line-clamp-3 mb-6 font-normal leading-relaxed">
                                        Understand statutory 12.07% holiday accrual, Working Time Regulations, and how to calculate leave balances.
                                    </p>
                                </div>
                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-[#64748B] font-medium">
                                    <span className="flex items-center gap-1"><Clock size={13} /> 5 min read</span>
                                    <span className="flex items-center gap-1 font-bold text-blue-700 group-hover:translate-x-1 transition-transform">Read Guide <ArrowRight size={14} /></span>
                                </div>
                            </div>

                            <div
                                onClick={() => navigate('/resources/stop-buddy-punching-without-expensive-hardware')}
                                className="group bg-white rounded-3xl p-7 shadow-md hover:shadow-xl border border-[#E8E2D8] transition-all cursor-pointer flex flex-col justify-between hover:-translate-y-1"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full text-[11px] font-black uppercase tracking-wider">
                                            Time Clock Tech
                                        </span>
                                        <Lock size={20} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-[#0F172A] group-hover:text-blue-700 transition-colors leading-snug">
                                        How to Stop Buddy Punching Without Expensive Hardware
                                    </h3>
                                    <p className="text-sm text-[#475569] line-clamp-3 mb-6 font-normal leading-relaxed">
                                        Eliminate time theft overnight using basic tablets, 4-digit PIN kiosks, and GPS geofencing.
                                    </p>
                                </div>
                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-[#64748B] font-medium">
                                    <span className="flex items-center gap-1"><Clock size={13} /> 4 min read</span>
                                    <span className="flex items-center gap-1 font-bold text-blue-700 group-hover:translate-x-1 transition-transform">Read Guide <ArrowRight size={14} /></span>
                                </div>
                            </div>

                            <div
                                onClick={() => navigate('/resources/paper-timesheets-vs-digital-time-tracking')}
                                className="group bg-white rounded-3xl p-7 shadow-md hover:shadow-xl border border-[#E8E2D8] transition-all cursor-pointer flex flex-col justify-between hover:-translate-y-1"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="px-3 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full text-[11px] font-black uppercase tracking-wider">
                                            Workflow & Payroll
                                        </span>
                                        <FileCheck size={20} className="text-slate-400 group-hover:text-indigo-600 transition-colors" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-[#0F172A] group-hover:text-blue-700 transition-colors leading-snug">
                                        Paper Timesheets vs Digital Time Tracking: What UK SMEs Need
                                    </h3>
                                    <p className="text-sm text-[#475569] line-clamp-3 mb-6 font-normal leading-relaxed">
                                        Compare human math errors, HMRC compliance rules, and payroll prep times between paper and digital software.
                                    </p>
                                </div>
                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-[#64748B] font-medium">
                                    <span className="flex items-center gap-1"><Clock size={13} /> 6 min read</span>
                                    <span className="flex items-center gap-1 font-bold text-blue-700 group-hover:translate-x-1 transition-transform">Read Guide <ArrowRight size={14} /></span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <button
                                onClick={() => navigate('/resources')}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0F172A] font-bold rounded-2xl border border-[#E8E2D8] hover:border-teal-600 transition-all text-sm shadow-xs hover:shadow-md"
                            >
                                View all UK employer guides <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </AnimatedSection>

                {/* 13. FINAL CTA */}
                <section className="bg-[#0F172A] text-white py-24 text-center relative overflow-hidden" id="final-cta">
                    <div className="container mx-auto px-6 relative z-10 max-w-3xl">
                        <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight text-white">
                            Start simplifying workforce management with KithHR.
                        </h2>
                        <p className="text-lg text-slate-300 mb-10 max-w-xl mx-auto font-medium leading-relaxed">
                            Prepare accurate attendance and payroll information for your accountant or payroll provider in minutes. Free forever for up to 15 employees.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-10 py-4.5 rounded-2xl text-lg shadow-xl transition-all"
                            >
                                Start Free Today →
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-[#080E1E] border-t border-slate-800/80 pt-16 pb-12 text-sm text-slate-400">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-slate-800/60">
                        {/* Col 1: Brand & Founder */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <img src="/logo.png" alt="KithHR Logo" width="32" height="32" className="w-8 h-8 object-contain" />
                                <span className="font-bold text-xl text-white">KithHR</span>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed">
                                UK staff scheduling, PIN time clock kiosk, and payroll-ready reporting for modern site teams.
                            </p>
                            <div className="pt-2">
                                <a 
                                    href="https://www.linkedin.com/in/stanislav-stefanov-1104823b9/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white transition bg-slate-900 px-3 py-1.5 rounded-full border border-slate-800"
                                >
                                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                                    <span>Founded by Stanislav Stefanov</span>
                                </a>
                            </div>
                        </div>

                        {/* Col 2: Navigation */}
                        <div>
                            <h3 className="font-extrabold text-white text-xs uppercase tracking-wider mb-4">Product Navigation</h3>
                            <ul className="space-y-2.5 text-xs text-slate-400">
                                <li><a href="#four-concepts" onClick={e => { e.preventDefault(); handleFooterNav('four-concepts'); }} className="hover:text-white transition">Core Benefits</a></li>
                                <li><a href="#see-in-action" onClick={e => { e.preventDefault(); handleFooterNav('see-in-action'); }} className="hover:text-white transition">Product Walkthrough</a></li>
                                <li><a href="#comparison" onClick={e => { e.preventDefault(); handleFooterNav('comparison'); }} className="hover:text-white transition">KithHR vs Manual</a></li>
                                <li><a href="#pricing" onClick={e => { e.preventDefault(); handleFooterNav('pricing'); }} className="hover:text-white transition">Pricing &amp; Free Plan</a></li>
                                <li><a href="#faq" onClick={e => { e.preventDefault(); handleFooterNav('faq'); }} className="hover:text-white transition">Frequently Asked Questions</a></li>
                            </ul>
                        </div>

                        {/* Col 3: Trust & Compliance */}
                        <div>
                            <h3 className="font-extrabold text-white text-xs uppercase tracking-wider mb-4">UK Standards</h3>
                            <ul className="space-y-2.5 text-xs text-slate-400">
                                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400 shrink-0" /> UK GDPR Data Hosting</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400 shrink-0" /> UK Employment Changes Ready</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400 shrink-0" /> Pre-Payroll Summary Exports</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400 shrink-0" /> Hardware-Agnostic PIN Kiosk</li>
                            </ul>
                        </div>

                        {/* Col 4: Trust & Reviews */}
                        <div className="space-y-4">
                            <h3 className="font-extrabold text-white text-xs uppercase tracking-wider mb-4">Connect &amp; Reviews</h3>
                            <div className="flex items-center gap-3">
                                <a 
                                    href="https://www.linkedin.com/in/stanislav-stefanov-1104823b9/" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    aria-label="KithHR Founder LinkedIn"
                                    className="p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition"
                                >
                                    <Linkedin size={18} />
                                </a>
                                <a 
                                    href="https://facebook.com" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    aria-label="KithHR Facebook Page"
                                    className="p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition"
                                >
                                    <Facebook size={18} />
                                </a>
                                <a 
                                    href="https://uk.trustpilot.com/review/kithhr.co.uk" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-3 py-2 bg-emerald-950/40 border border-emerald-800/40 text-emerald-400 rounded-xl text-xs font-bold hover:bg-emerald-900/40 transition"
                                >
                                    <Star size={14} className="fill-emerald-400 text-emerald-400" />
                                    <span>Rated on Trustpilot</span>
                                </a>
                            </div>
                            <p className="text-[11px] text-slate-400">
                                Direct inquiries: <a href="mailto:info@kithhr.co.uk" className="text-slate-300 hover:underline">info@kithhr.co.uk</a>
                            </p>
                        </div>
                    </div>

                    {/* Bottom Legal Bar */}
                    <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
                        <span className="text-slate-400">© 2026 KithHR. All rights reserved. Built for UK teams.</span>
                        <div className="flex items-center space-x-6 font-semibold">
                            <a href="/privacy-policy" onClick={e => { e.preventDefault(); navigate('/privacy-policy'); }} className="text-slate-400 hover:text-white transition">Privacy Policy</a>
                            <a href="/terms" onClick={e => { e.preventDefault(); navigate('/terms'); }} className="text-slate-400 hover:text-white transition">Terms of Service</a>
                            <a href="/refund-policy" onClick={e => { e.preventDefault(); navigate('/refund-policy'); }} className="text-slate-400 hover:text-white transition">Refund Policy</a>
                            <a href="mailto:info@kithhr.co.uk" className="text-slate-400 hover:text-white transition">info@kithhr.co.uk</a>
                        </div>
                    </div>
                </div>
            </footer>

            <StickyCTA />

            {localModal && (
                <LocalModal title={localModal.title} content={localModal.content} onClose={() => setLocalModal(null)} />
            )}
        </div>
    );
};

export default Landing;
